import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { searchSessions } from '../utils/api'
import { formatSessionDate } from '../utils/session-time'
import type {
  SessionOption,
  ScopeValue,
  RangeValue,
  SessionScopeState,
} from '../types'

type Props = SessionScopeState & {
  eventID: string
  // Rendered at the end of the row, e.g. a tab's "Invite Users" button.
  action?: ReactNode
}

let upcomingOptions: { value: RangeValue; label: string }[] = [
  { value: '1m', label: 'Next month' },
  { value: '3m', label: 'Next 3 months' },
  { value: '6m', label: 'Next 6 months' },
  { value: '12m', label: 'Next 12 months' },
  { value: 'all', label: 'All upcoming' },
]

let pastOptions: { value: RangeValue; label: string }[] = [
  { value: '1m', label: 'Past month' },
  { value: '3m', label: 'Past 3 months' },
  { value: '6m', label: 'Past 6 months' },
  { value: '12m', label: 'Past 12 months' },
  { value: 'all', label: 'All past' },
]

let SessionScopePicker = (props: Props) => {
  let {
    eventID,
    scope,
    range,
    singleSession,
    setScope,
    setRange,
    setSingleSession,
    action,
  } = props

  let [query, setQuery] = useState(singleSession?.title ?? '')
  let [debouncedQuery, setDebouncedQuery] = useState('')
  let debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    },
    [],
  )

  useEffect(() => {
    if (singleSession) {
      setQuery(singleSession.title)
      setDebouncedQuery('')
    } else {
      setQuery('')
      setDebouncedQuery('')
    }
  }, [singleSession?.id])

  useEffect(() => {
    if (scope !== 'single') {
      setQuery('')
      setDebouncedQuery('')
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [scope])

  let { data, isFetching } = useQuery({
    queryKey: ['session-scope-typeahead', eventID, debouncedQuery],
    queryFn: () => searchSessions(eventID, debouncedQuery).then((r) => r.items),
    enabled:
      scope === 'single' && debouncedQuery.trim().length > 0 && !singleSession,
    staleTime: 30_000,
  })

  let handleScopeChange = (_: React.MouseEvent, value: ScopeValue | null) => {
    if (!value) return
    setScope(value)
  }

  let rangeOptions = scope === 'past' ? pastOptions : upcomingOptions

  return (
    <Box
      className="flex flex-wrap items-center gap-4"
      sx={(theme) => ({
        paddingBottom: { xs: 2, sm: 0 },
        marginBottom: { xs: 1, sm: 0 },
        borderBottom: {
          xs: `1px solid ${theme.palette.divider}`,
          sm: 0,
        },
      })}
    >
      <ToggleButtonGroup
        color="primary"
        exclusive
        value={scope}
        onChange={handleScopeChange}
        aria-label="Session scope"
        className="w-full sm:w-auto"
        sx={{
          bgcolor: 'background.paperDark',
          borderRadius: 1,
          p: 0.5,
          '& .MuiToggleButton-root': {
            flexGrow: 1,
            border: 0,
            borderRadius: 1,
            textTransform: 'none',
            px: 2,
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          },
          '& .Mui-selected': {
            bgcolor: 'action.selected',
          },
        }}
      >
        <ToggleButton value="upcoming">Upcoming</ToggleButton>
        <ToggleButton value="past">Past</ToggleButton>
        <ToggleButton value="single">Single session</ToggleButton>
      </ToggleButtonGroup>

      {scope !== 'single' && (
        <Select
          value={range}
          onChange={(e) => setRange(e.target.value as RangeValue)}
          slotProps={{ input: { 'aria-label': 'Session range' } }}
          className="flex-1 sm:flex-none"
          sx={{ minWidth: 160, backgroundColor: 'background.paperDark' }}
        >
          {rangeOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      )}

      {scope === 'single' && (
        <Autocomplete<SessionOption>
          className="flex-1 sm:flex-none"
          sx={{ minWidth: 280 }}
          options={data ?? []}
          value={singleSession}
          inputValue={query}
          loading={isFetching}
          filterOptions={(x) => x}
          getOptionLabel={(o) => o.title}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          onInputChange={(_, v, reason) => {
            if (reason === 'input') {
              setQuery(v)
              if (debounceRef.current) clearTimeout(debounceRef.current)
              // eslint-disable-next-line fp/no-mutation
              debounceRef.current = setTimeout(() => setDebouncedQuery(v), 300)
            }
            if (reason === 'clear') {
              setQuery('')
              setDebouncedQuery('')
              if (debounceRef.current) clearTimeout(debounceRef.current)
            }
          }}
          onChange={(_, v) => setSingleSession(v)}
          noOptionsText={
            query.length === 0
              ? 'Type to search sessions'
              : 'No sessions found.'
          }
          renderOption={(renderProps, option) => (
            <li {...renderProps} key={option.id}>
              <ListItemText
                primary={option.title}
                secondary={formatSessionDate(option.ts, option.timeZone)}
              />
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search sessions…"
              sx={{ backgroundColor: 'background.paperDark' }}
              slotProps={{
                ...params.slotProps,
                htmlInput: {
                  ...(params.slotProps?.htmlInput as object),
                  'aria-label': 'Search sessions',
                },
              }}
            />
          )}
        />
      )}

      {action && <Box className="sm:ml-auto">{action}</Box>}
    </Box>
  )
}

export default SessionScopePicker
